import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ParameterList, handleErrorToast } from 'src/components/molecules';
import {
  GroupType,
  useDeleteGroupMutation,
  useGroupUserCountQuery,
  useUpdateGroupMutation,
} from 'src/graphql';

export const useGroup = (group: GroupType) => {
  const { id: groupId, attributes, name } = group;
  const queryClient = useQueryClient();
  const {
    data: groupUserCount,
    refetch: refetchGroupUserCount,
    isLoading: isGroupUserCountLoading,
  } = useGroupUserCountQuery({
    groupUserCountId: groupId,
  });
  const { mutate: updateGroup } = useUpdateGroupMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['Groups'] });
      refetchGroupUserCount();
    },
  });
  const { mutate: deleteGroup } = useDeleteGroupMutation({
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['Groups'] });
    },
    onError: (error: unknown) => {
      if (
        (error as Error).message.includes('routing_rules_destinationGroup_fkey')
      ) {
        handleErrorToast({
          toastMessage: `Failed to delete group ${name} as it is assigned to a routing rule.`,
        });
      }

      if (
        (error as Error).message.includes('organisations_defaultGroup_fkey')
      ) {
        handleErrorToast({
          toastMessage: `Failed to delete group ${name} as it is assigned to an organisations routing default.`,
        });
      }
    },
  });

  const parameterOptions = [
    { value: 'site', label: 'Site', pluralLabel: 'Sites' },
    { value: 'department', label: 'Department', pluralLabel: 'Departments' },
    { value: 'country', label: 'Country', pluralLabel: 'Countries' },
    { value: 'team', label: 'Team members', pluralLabel: 'Team members' },
    {
      value: 'systemRoles',
      label: 'Permissions',
      pluralLabel: 'Permissions',
    },
  ];

  const serverParameterList: ParameterList = useMemo(
    () =>
      attributes && Object.keys(attributes).length > 0
        ? attributes
        : { '': [] },
    [attributes],
  );

  const [parameterList, setParameterList] =
    useState<ParameterList>(serverParameterList);
  const lastServerParameterListRef = useRef<ParameterList>(serverParameterList);

  useEffect(() => {
    const userHasUnsavedChanges = !isEqual(
      parameterList,
      lastServerParameterListRef.current,
    );

    if (!userHasUnsavedChanges) {
      setParameterList(serverParameterList);
    }

    lastServerParameterListRef.current = serverParameterList;
  }, [parameterList, serverParameterList]);

  return {
    groupUserCount,
    isGroupUserCountLoading,
    updateGroup,
    deleteGroup,
    parameterList,
    setParameterList,
    parameterOptions,
  };
};
